import { useState, useEffect, cloneElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';

async function ensureProfile(userId, email, name, picture) {
  await supabase.from('profiles').upsert(
    { id: userId, email, name, picture },
    { onConflict: 'id', ignoreDuplicates: true }
  );
}

function hasAccess(subscription) {
  if (!subscription) return false;

  // Assinatura paga ativa
  if (subscription.status === 'active') return true;

  // Trial válido — só se foi criado pelo Stripe (tem stripe_customer_id)
  if (subscription.status === 'trial') {
    const trialEnd = subscription.trial_ends_at
      ? new Date(subscription.trial_ends_at)
      : null;
    if (!trialEnd) return false;
    return new Date() < trialEnd;
  }

  return false;
}

export default function ProtectedRoute({ children }) {
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', user: null, subscription: null });

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session) {
        navigate('/', { replace: true });
        return;
      }

      const u = session.user;
      const userObj = {
        user_id: u.id,
        email: u.email,
        name: u.user_metadata?.full_name || u.email,
        picture: u.user_metadata?.avatar_url || '',
      };

      // Garante perfil
      await ensureProfile(u.id, userObj.email, userObj.name, userObj.picture);

      // Busca assinatura — NÃO cria automaticamente
      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', u.id)
        .maybeSingle();

      if (!hasAccess(subscription)) {
        // Sem acesso válido → vai para planos
        navigate('/planos', { replace: true });
        return;
      }

      setState({ status: 'ready', user: userObj, subscription });
    });

    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (!session) navigate('/', { replace: true });
      }
    );

    return () => authSub.unsubscribe();
  }, [navigate]);

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: "var(--green)" }} />
          <p className="text-sm" style={{ color: "var(--text-3)" }}>Verificando acesso...</p>
        </div>
      </div>
    );
  }

  return cloneElement(children, { user: state.user, subscription: state.subscription });
}
